import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { request } from '../../Utils/request';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { calculateDistance } from '../../Utils/googleMapsDistance';

interface Place {
  _id: string;
  name: string;
  description: string;
  categories: string[];
  tags: string[];
  type: string;
  featured: boolean;
  location: {
    type: string;
    coordinates?: [number, number];
    lat?: number;
    lng?: number;
    zoom: number;
  };
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    zipcode: string;
  };
  owner_details: {
    name: string;
    email: string;
    phone: string;
  };
  files: Array<{
    fileURL: string;
    fileType: string;
  }>;
  rating?: number;
  distance?: string;
  distanceValue?: number;
  createdAt: string;
  updatedAt: string;
  isLiked: boolean;

}

interface FavoritePlacesResponse {
  success: boolean;
  data: Place[];
  total: number;
}

const FavoritePlaces: React.FC = () => {
  const navigate = useNavigate();
  const [places, setPlaces] = useState<Place[]>([]);
  const [placesWithDistance, setPlacesWithDistance] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const userLocation = useSelector((state: RootState) => state.location.location);

  useEffect(() => {
    fetchFavoritePlaces();
  }, []);

  const fetchFavoritePlaces = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await request<FavoritePlacesResponse>('/places/liked-places', {
        method: 'GET',
        authorization: true,
      });

      if (response.success === true) {
        setPlaces(response.data);
      } else {
        setError('Failed to fetch favorite places');
      }
    } catch (err) {
      console.error('Error fetching favorite places:', err);
      setError('Failed to load favorite places. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const computeDistances = async () => {
      if (!places.length) {
        if (isMounted) setPlacesWithDistance(places);
        return;
      }

      if (!userLocation?.lat || !userLocation?.lng) {
        if (isMounted) setPlacesWithDistance(places);
        return;
      }

      try {
        const updatedPlaces = await Promise.all(
          places.map(async (place) => {
            const placeLat =
              place.location?.coordinates && place.location.coordinates.length >= 2
                ? place.location.coordinates[1]
                : place.location?.lat;
            const placeLng =
              place.location?.coordinates && place.location.coordinates.length >= 2
                ? place.location.coordinates[0]
                : place.location?.lng;

            if (placeLat == null || placeLng == null) {
              return { ...place, distance: 'N/A', distanceValue: Infinity };
            }

            try {
              const distance = await calculateDistance(
                userLocation.lat,
                userLocation.lng,
                placeLat,
                placeLng
              );
              return {
                ...place,
                distance: `${distance.toFixed(1)}km`,
                distanceValue: distance,
              };
            } catch (err) {
              console.warn(`Failed to compute distance for ${place.name}`, err);
              return { ...place, distance: 'N/A', distanceValue: Infinity };
            }
          })
        );

        const sortedPlaces = updatedPlaces.sort(
          (a, b) => (a.distanceValue ?? Infinity) - (b.distanceValue ?? Infinity)
        );

        if (isMounted) setPlacesWithDistance(sortedPlaces);
      } catch (err) {
        console.error('Error computing distances for favorite places:', err);
        if (isMounted) setPlacesWithDistance(places);
      }
    };

    computeDistances();

    return () => {
      isMounted = false;
    };
  }, [places, userLocation]);

  const handleFavoriteToggle = (placeId: string) => {
    setPlaces((prev) => prev.filter((place) => place._id !== placeId));
  };

  const placesToRender = placesWithDistance.length ? placesWithDistance : places;

  const handlePlaceClick = (placeId: string) => {
    navigate(`/places/${placeId}`);
  };

  const getStatusStyles = (status?: string) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'approved') {
      return { bgcolor: '#1B5E20', label: 'Approved' };
    }
    return { bgcolor: '#FF6B35', label: 'Pending' }; // Orange color for pending
  };

  if (loading) {
    return (
      <Box sx={{ 
        bgcolor: '#fff', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <TopNavBar title="Saved Places" canGoBack />
        <Container maxWidth="sm" sx={{ flex: 1, py: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        bgcolor: '#fff', 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <TopNavBar title="Saved Places" canGoBack />
        <Container maxWidth="sm" sx={{ flex: 1, py: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: '#fff', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <TopNavBar title="Saved Places" canGoBack />
      
      <Container maxWidth="sm" sx={{ flex: 1, py: 2 }}>
        {placesToRender?.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '50vh',
            textAlign: 'center'
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Saved Places
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Places you save will appear here
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {placesToRender.map((place) => {
              // const statusStyles = getStatusStyles(place.status);
              const categoryName = place.categories?.[0] || place.type || 'Place';
              
              return (
                <Card
                  key={place._id}
                  onClick={() => handlePlaceClick(place._id)}
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'row',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  {/* Image thumbnail */}
                  <CardMedia
                    component="img"
                    sx={{
                      width: 100,
                      height: 100,
                      objectFit: 'cover',
                      borderRadius: '8px 0 0 8px',
                    }}
                    image={place.files?.[0]?.fileURL || '/placeholder-image.jpg'}
                    alt={place.name}
                  />
                  
                  {/* Content area */}
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      gap: 2,
                    }}
                  >
                    {/* Text content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          color: '#000',
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {place.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '14px',
                          color: '#757575',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {categoryName}
                      </Typography>
                    </Box>
                    
                    {/* Status button */}
                    {/* <Chip
                      label={statusStyles.label}
                      sx={{
                        bgcolor: statusStyles.bgcolor,
                        color: '#fff',
                        borderRadius: '20px',
                        px: 1.5,
                        py: 0.5,
                        height: 'auto',
                        fontSize: '14px',
                        fontWeight: 500,
                        minWidth: 'auto',
                        flexShrink: 0,
                      }}
                    /> */}
                  </Box>
                </Card>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default FavoritePlaces;
